import React, { useEffect, useRef, useState } from 'react';
import {
  getChannels, createChannel, deleteChannel, deleteChannelHard, deleteChannelHardCascade,
  enableChannel, testChannel,
  channelWebhookInfo, reregisterChannelWebhook,
} from '../api.js';

// Channels = bot/business connectors managed centrally with credentials.
// Personal numbers (telegram_personal / whatsapp_personal) live on the
// Sessions page — they have their own lifecycle (QR / pairing code,
// per-session worker container), which is what Sessions handles. Showing
// them here would duplicate the UI and the "Проверить" button would always
// return failed because personal sessions aren't adapter-backed.
const CHANNEL_TYPES = [
  { value: 'telegram_bot', label: 'Telegram Bot' },
  { value: 'whatsapp_business', label: 'WhatsApp Business' },
  { value: 'max_bot', label: 'MAX Bot' },
];
const PERSONAL_TYPES = new Set(['telegram_personal', 'whatsapp_personal', 'max_personal']);


function channelTypeLabel(value) {
  const labels = {
    telegram_bot: 'Telegram — бот',
    max_bot: 'MAX — бот',
    whatsapp_business: 'WhatsApp — бизнес',
    whatsapp_bot: 'WhatsApp — бот',
  };

  return labels[value] || value;
}

function channelStatusLabel(value) {
  const key = String(value || '')
    .trim()
    .toLowerCase();

  const labels = {
    active: 'Активен',
    enabled: 'Активен',
    online: 'Подключён',
    disabled: 'Отключён',
    inactive: 'Отключён',
    error: 'Ошибка',
  };

  return labels[key] || value;
}


export default function Channels() {
  const [channels, setChannels] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ name: '', channel_type: 'telegram_bot', credentials: {}, bitrix_line_id: '' });
  const [testResults, setTestResults] = useState({});
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);  // shows inline "Delete? [Yes/No]"
  const [toast, setToast] = useState(null);                       // {type, text}
  const [createErr, setCreateErr] = useState(null);
  // Per-channel webhook diagnostics — populated on click of "Обновить связь".
  // Keyed by channel id so multiple channels can show their info row at
  // once. Each value is the response from /webhook-info or an error string.
  const [webhookInfo, setWebhookInfo] = useState({});
  // Persist the toast-dismiss timer so we can cancel it on the next toast
  // or component unmount — otherwise React 18 warns about setState on an
  // unmounted component and stacked timers race each other.
  const toastTimerRef = useRef(null);

  const showToast = (text, type = 'info') => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToast({ text, type });
    toastTimerRef.current = setTimeout(() => setToast(null), 4500);
  };
  useEffect(() => () => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
  }, []);

  useEffect(() => {
    if (!showModal) return undefined;

    const handleModalKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        setShowModal(false);
        setCreateErr(null);
        return;
      }

      if (event.key !== 'Backspace') return;

      const target = event.target;
      const editable = (
        target instanceof HTMLInputElement
        || target instanceof HTMLTextAreaElement
        || target?.isContentEditable
      );

      // Backspace inside an editor must keep its normal text-editing
      // behaviour. Outside an editor it must never navigate back or
      // dismiss the channel modal.
      if (!editable) {
        event.preventDefault();
        event.stopPropagation();
      }
    };

    window.addEventListener(
      'keydown',
      handleModalKeyDown,
      true,
    );

    return () => window.removeEventListener(
      'keydown',
      handleModalKeyDown,
      true,
    );
  }, [showModal]);

  // Filter out personal sessions — they're managed under the Sessions tab.
  const load = () => getChannels()
    .then((list) => setChannels(list.filter((c) => !PERSONAL_TYPES.has(c.channel_type))))
    .catch(() => {});
  useEffect(() => { load(); }, []);

  const handleCreate = async () => {
    setCreateErr(null);
    try {
      await createChannel({
        ...form,
        // Empty bitrix_line_id is a literal string "" — backend may store
        // that and then mismatch downstream `WHERE bitrix_line_id IS NULL`
        // queries. Omit instead.
        bitrix_line_id: form.bitrix_line_id?.trim() || undefined,
        credentials: Object.keys(form.credentials).length ? form.credentials : undefined,
      });
      setShowModal(false);
      setForm({ name: '', channel_type: 'telegram_bot', credentials: {}, bitrix_line_id: '' });
      load();
    } catch (err) {
      // 409 = duplicate identity (bot_token / phone). Surface backend message.
      const msg = String(err.message || err);
      const detail = msg.match(/"detail":"([^"]+)"/)?.[1] || msg;
      setCreateErr(detail);
    }
  };

  // Soft disable: channel.is_active = false; history preserved; re-enable possible.
  const handleDisable = async (id) => {
    try {
      await deleteChannel(id);
      showToast('Канал отключён', 'info');
      load();
    } catch (err) {
      showToast(`Disable failed: ${err.message}`, 'error');
    }
  };

  const handleEnable = async (id) => {
    try {
      await enableChannel(id);
      showToast('Канал включён', 'info');
      load();
    } catch (err) {
      showToast(`Enable failed: ${err.message}`, 'error');
    }
  };

  // Hard delete: physical removal. Backend refuses (409) if dialogs
  // still exist — we then surface a second confirm offering cascade.
  // This two-step UX prevents «one wrong click wipes 1000 dialogs» but
  // keeps it possible for test bots with one stray dialog.
  const handleDelete = async (id, opts = {}) => {
    setConfirmDeleteId(null);
    try {
      if (opts.cascade) {
        await deleteChannelHardCascade(id);
      } else {
        await deleteChannelHard(id);
      }
      // Drop the now-stale Test result for this channel so a future
      // channel that happens to reuse the UUID (rare with random UUIDs
      // but possible after a DB reset) doesn't inherit a misleading
      // `connected`/`failed` badge.
      setTestResults((prev) => {
        const { [id]: _drop, ...rest } = prev;
        return rest;
      });
      // Same cleanup for the webhook diagnostics row — otherwise a re-added
      // channel with the same UUID would inherit a stale "Last error".
      setWebhookInfo((prev) => {
        const { [id]: _drop2, ...rest } = prev;
        return rest;
      });
      showToast('Channel deleted', 'info');
      load();
    } catch (err) {
      const detail = String(err.message || '').match(/"detail":"([^"]+)"/)?.[1] || err.message;
      // 409 path: backend tells us how many dialogs are blocking. Offer
      // cascade, but ONLY once (no infinite loop) and ONLY on the
      // explicit conflict — other 4xx/5xx still surface as a plain toast.
      const is409 = String(err.message || '').startsWith('409');
      const blocksHistory = is409 && /\d+\s+dialogs/.test(detail);
      if (blocksHistory && !opts.cascade) {
        const match = detail.match(/(\d+)\s+dialogs/);
        const count = match ? match[1] : '?';
        const yes = window.confirm(
          `Канал содержит ${count} диалогов с историей сообщений.\n\n` +
          `Удалить ВСЕ диалоги, сообщения И сам канал безвозвратно?\n` +
          `Контакты сохранятся (могут быть привязаны к другим каналам).`
        );
        if (yes) {
          await handleDelete(id, { cascade: true });
          return;
        }
      }
      showToast(`Delete failed: ${detail}`, 'error');
    }
  };

  const handleTest = async (id) => {
    try {
      const result = await testChannel(id);
      setTestResults((prev) => ({ ...prev, [id]: result.ok ? 'ok' : 'fail' }));
    } catch {
      setTestResults((prev) => ({ ...prev, [id]: 'fail' }));
    }
  };

  // Toggle the diagnostics row: second click hides it so the table doesn't
  // accumulate noise. Errors are captured in-row instead of as toasts
  // because operators usually want to see them next to the channel.
  const handleWebhookInfo = async (id) => {
    if (webhookInfo[id]) {
      setWebhookInfo((prev) => {
        const { [id]: _drop, ...rest } = prev;
        return rest;
      });
      return;
    }
    try {
      const info = await channelWebhookInfo(id);
      setWebhookInfo((prev) => ({ ...prev, [id]: info }));
    } catch (err) {
      setWebhookInfo((prev) => ({ ...prev, [id]: { error: err.message } }));
    }
  };

  const handleReregister = async (id) => {
    try {
      const res = await reregisterChannelWebhook(id);
      showToast(res.ok ? `Re-registered: ${res.webhook_url}` : 'Re-register failed', res.ok ? 'info' : 'error');
      // Refresh diagnostics row if it's currently open
      if (webhookInfo[id]) {
        try {
          const info = await channelWebhookInfo(id);
          setWebhookInfo((prev) => ({ ...prev, [id]: info }));
        } catch { /* keep stale info on refresh error */ }
      }
    } catch (err) {
      showToast(`Re-register failed: ${err.message}`, 'error');
    }
  };

  const credentialFields = {
    telegram_bot: ['bot_token'],
    whatsapp_business: ['phone_number_id', 'access_token'],
    max_bot: ['bot_token'],
    telegram_personal: ['phone_number'],
    whatsapp_personal: ['phone_number'],
  };

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div className="mf-subpage-heading">
          <div>
            <h2>Боты и интеграции</h2>
            <span>
              Telegram Bot · MAX Bot · WhatsApp Business API
            </span>
          </div>
        </div>
        <button className="btn-primary" onClick={() => setShowModal(true)}>+ Добавить канал</button>
      </div>

      {toast && (
        <div style={{
          marginBottom: 16, padding: '10px 14px', borderRadius: 6,
          background: toast.type === 'error' ? '#fed7d7' : '#bee3f8',
          color: toast.type === 'error' ? '#742a2a' : '#2c5282',
        }}>{toast.text}</div>
      )}

      <div className="card">
        <table>
          <thead>
            <tr>
              <th>Название</th>
              <th>Тип</th>
              <th>Открытая линия</th>
              <th>Статус</th>
              <th>Действия</th>
            </tr>
          </thead>
          <tbody>
            {channels.length === 0 && (
              <tr><td colSpan={5} style={{ textAlign: 'center', color: '#a0aec0' }}>Каналы ещё не добавлены</td></tr>
            )}
            {channels.map((ch) => (
              <React.Fragment key={ch.id}>
              <tr>
                <td><strong>{ch.name}</strong></td>
                <td>{channelTypeLabel(ch.channel_type)}</td>
                <td>{ch.bitrix_line_id || '-'}</td>
                <td>
                  <span className={`badge ${ch.is_active ? 'badge-ok' : 'badge-error'}`}>
                    {ch.is_active ? 'Активен' : 'Отключён'}
                  </span>
                  {testResults[ch.id] && (
                    <span className={`badge ${testResults[ch.id] === 'ok' ? 'badge-ok' : 'badge-error'}`} style={{ marginLeft: 6 }}>
                      {testResults[ch.id] === 'ok' ? 'connected' : 'failed'}
                    </span>
                  )}
                </td>
                <td>
                  {confirmDeleteId === ch.id ? (
                    <div className="btn-group">
                      <span style={{ fontSize: 13, color: '#742a2a', marginRight: 8 }}>
                        Delete permanently?
                      </span>
                      <button className="btn-danger btn-sm" onClick={() => handleDelete(ch.id)}>
                        Yes, delete
                      </button>
                      <button className="btn-ghost btn-sm" onClick={() => setConfirmDeleteId(null)}>
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <div className="btn-group">
                      <button
                        className="btn-ghost btn-sm"
                        title="Send a health-check request to the messenger API to verify credentials"
                        onClick={() => handleTest(ch.id)}
                      >Проверить</button>
                      {ch.channel_type === 'telegram_bot' && (
                        <button
                          className="btn-ghost btn-sm"
                          title="Show Telegram's view of our webhook: registered URL, pending updates, last delivery error. The #1 diagnostic when a bot 'isn't receiving anything'."
                          onClick={() => handleWebhookInfo(ch.id)}
                        >Обновить связь</button>
                      )}
                      {ch.is_active ? (
                        <button
                          className="btn-ghost btn-sm"
                          title="Stop the channel (no new messages will be ingested). History is preserved; can be re-enabled."
                          onClick={() => handleDisable(ch.id)}
                        >Отключить</button>
                      ) : (
                        <button
                          className="btn-primary btn-sm"
                          title="Re-enable a disabled channel"
                          onClick={() => handleEnable(ch.id)}
                        >Включить</button>
                      )}
                      <button
                        className="btn-danger btn-sm"
                        title="Permanently delete the channel from the database. Refused if dialogs still exist."
                        onClick={() => setConfirmDeleteId(ch.id)}
                      >🗑</button>
                    </div>
                  )}
                </td>
              </tr>
              {webhookInfo[ch.id] && (
                <tr>
                  <td colSpan={5} style={{ background: '#f7fafc', padding: 14, fontSize: 13 }}>
                    {webhookInfo[ch.id].error ? (
                      <div style={{ color: '#c53030' }}>
                        <strong>Webhook check failed:</strong> {webhookInfo[ch.id].error}
                      </div>
                    ) : (
                      <div>
                        <div style={{ marginBottom: 8 }}>
                          <strong>Registered URL:</strong>{' '}
                          <code style={{ background: '#edf2f7', padding: '2px 6px', borderRadius: 3 }}>
                            {webhookInfo[ch.id].registered_url || '(none — setWebhook never succeeded)'}
                          </code>
                          {webhookInfo[ch.id].mismatch && (
                            <span className="badge badge-error" style={{ marginLeft: 8 }}>mismatch</span>
                          )}
                        </div>
                        <div style={{ marginBottom: 8 }}>
                          <strong>Expected:</strong>{' '}
                          <code style={{ background: '#edf2f7', padding: '2px 6px', borderRadius: 3 }}>
                            {webhookInfo[ch.id].expected_url}
                          </code>
                        </div>
                        <div style={{ marginBottom: 8 }}>
                          <strong>Pending updates:</strong> {webhookInfo[ch.id].pending_update_count ?? 0}
                          {' • '}
                          <strong>Secret configured:</strong> {webhookInfo[ch.id].secret_configured ? 'yes' : 'no'}
                        </div>
                        {webhookInfo[ch.id].last_error_message && (
                          <div style={{
                            marginTop: 6, padding: 8, background: '#fff5f5',
                            border: '1px solid #fed7d7', borderRadius: 4, color: '#742a2a',
                          }}>
                            <strong>Last Telegram delivery error:</strong>{' '}
                            {webhookInfo[ch.id].last_error_message}
                            {webhookInfo[ch.id].last_error_date && (
                              <span style={{ marginLeft: 6, opacity: 0.7 }}>
                                ({new Date(webhookInfo[ch.id].last_error_date * 1000).toLocaleString()})
                              </span>
                            )}
                          </div>
                        )}
                        <div style={{ marginTop: 10 }}>
                          <button
                            className="btn-primary btn-sm"
                            title="Re-run setWebhook with current APP_BASE_URL and TELEGRAM_WEBHOOK_SECRET. Use after rotating the secret or moving domains."
                            onClick={() => handleReregister(ch.id)}
                          >Re-register webhook</button>
                        </div>
                      </div>
                    )}
                  </td>
                </tr>
              )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div
          className="modal-overlay"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              setShowModal(false);
              setCreateErr(null);
            }
          }}
        >
          <div
            className="modal"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <h2>Новый канал</h2>
            {createErr && (
              <div style={{
                background: '#fff5f5', color: '#c53030',
                padding: 10, marginBottom: 12, borderRadius: 6, fontSize: 14,
              }}>{createErr}</div>
            )}
            <div className="form-grid">
              <div className="form-group">
                <label>Название канала</label>
                <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Например, Telegram отдела продаж" />
              </div>
              <div className="form-group">
                <label>Тип подключения</label>
                <select value={form.channel_type} onChange={(e) => setForm({ ...form, channel_type: e.target.value, credentials: {} })}>
                  {CHANNEL_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>
              <div className="form-group full">
                <label>Открытая линия Bitrix24</label>
                <input value={form.bitrix_line_id} onChange={(e) => setForm({ ...form, bitrix_line_id: e.target.value })} placeholder="1" />
              </div>
              {(credentialFields[form.channel_type] || []).map((field) => (
                <div className="form-group full" key={field}>
                  <label>{field}</label>
                  <input
                    type={field.includes('token') || field.includes('secret') ? 'password' : 'text'}
                    value={form.credentials[field] || ''}
                    onChange={(e) => setForm({ ...form, credentials: { ...form.credentials, [field]: e.target.value } })}
                    placeholder={field}
                  />
                </div>
              ))}
            </div>
            <div className="modal-actions">
              <button className="btn-ghost" onClick={() => { setShowModal(false); setCreateErr(null); }}>Отмена</button>
              <button className="btn-primary" onClick={handleCreate} disabled={!form.name}>Создать</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
